import { Dayjs } from 'dayjs';

import { isOneShotCampaign, isPeriodicCampaign } from '../../newsletters-module/utils/campaign.utils';

export interface CampaignConfigId {
  engineId: number;
  source: string;
  id: string;
}

/**
 * this would run twice daily, at 4:30 AM and 4:30 PM:
 * {
 *   "hours": [4, 16],
 *   "minutes": [30]
 * }
 *
 * minutes: 0 - 59
 * hours: 0 - 23
 * weekdays: 0 - 6 | Sunday is 0, Saturday is 6
 * days: 1 - 31
 * months: 1 - 12
 */
export interface PeriodicTiming {
  minutes?: number[];
  hours?: number[];
  days?: number[];
  months?: number[];
  daysOfWeek?: number[];
}

export interface CampaignExecutionEvent {
  campaignId: string;
  date: Dayjs;
  isEnabled: boolean;
  editorialTimestamp?: number;
}

export enum CampaignType {
  Periodic = 'periodic',
  OneShot = 'oneshot'
}

export interface CampaignConfig {
  id: CampaignConfigId;
  _type: CampaignType;
  enabled: boolean;
  cronicleId: string;
  isTestSending: boolean;
}

export interface EditorialPlanification {
  engineId: number,
  sourceId: string,
  timestamp: number
}

export interface PeriodicCampaignConfig extends CampaignConfig {
  timing: PeriodicTiming;
}

export interface OneShotCampaignConfig extends CampaignConfig {
  timestamp: number;
  overrides?: CampaignConfigId;
}

export class CampaignConfigUtils {

  static dayOfWeekToString(daysOfWeek: number): string {
    switch (daysOfWeek) {
      case 0: return 'dimanche';
      case 1: return 'lundi';
      case 2: return 'mardi';
      case 3: return 'mercredi';
      case 4: return 'jeudi';
      case 5: return 'vendredi';
      case 6: return 'samedi';
    }
  }

  static timeToString(timing: PeriodicTiming) {
    if (!timing.hours || !timing.minutes) {
      return '';
    } else if (timing.hours.length === 1 && timing.minutes.length === 1) {
      return timing.hours[0].toString().padStart(2, '0') + 'h' + timing.minutes[0].toString().padStart(2, '0');
    } else {
      return timing.hours.map(hour => hour.toString().padStart(2, '0') + 'h' + timing.minutes[0].toString().padStart(2, '0')).join(', ');
    }
  }

  static campaignToString(campaign: CampaignConfig): string {
    if (isOneShotCampaign(campaign)) {
      const oneShot = new Date(campaign.timestamp);
      return `Exceptionnellement, le ${oneShot.getDate().toString().padStart(2, '0')}/${(oneShot.getMonth() + 1).toString().padStart(2, '0')} à ${oneShot.getHours().toString().padStart(2, '0')}h${oneShot.getMinutes().toString().padStart(2, '0')}`;
    } else if (isPeriodicCampaign(campaign)) {

      if (!campaign.timing?.daysOfWeek?.length && !campaign.timing?.days?.length && !campaign.timing?.months?.length) {

        return 'Tous les jours, à ' + CampaignConfigUtils.timeToString(campaign.timing);

      } else if (campaign.timing?.daysOfWeek?.length) {
        const daysOfWeek = campaign.timing.daysOfWeek.map(CampaignConfigUtils.dayOfWeekToString).join(', ');
        return `Chaque semaine, le ${daysOfWeek} à ${CampaignConfigUtils.timeToString(campaign.timing)}`;
      }
    }
  }

}
